<script setup lang="ts">
import PerfectScrollbar from "perfect-scrollbar";
import {onBeforeUnmount, onMounted, reactive, ref, watch} from "vue";
import CasesCompleted from "@/components/modules/Analytics/AnalyticsByParam/CasesCompleted.vue";
import CasesSentToCourt from "@/components/modules/Analytics/AnalyticsByParam/CasesSentToCourt.vue";
import CasesSuspended from "@/components/modules/Analytics/AnalyticsByParam/CasesSuspended.vue";
import CasesReturned from "@/components/modules/Analytics/AnalyticsByParam/CasesReturned.vue";
import CasesAccepted from "@/components/modules/Analytics/AnalyticsByParam/CasesAccepted.vue";
import AccusedInCourt from "@/components/modules/Analytics/AnalyticsByParam/AccusedInCourt.vue";
import PersonsRehabilitated from "@/components/modules/Analytics/AnalyticsByParam/PersonsRehabilitated.vue";
import {District, Month, Region} from "@/types/files";
import {AnalyticsState, LocationType} from "@/types/analytics";
import {MapDisplayParamsKey} from "@/composables/constants/params";
import {useAnalyticStore} from "@/stores/analytics";
import {DynamicQuery, RegionTopQuery, TotalMetricDataQuery} from "@/types/analytics/requests";

import type {
  CasesAcceptedAnalytics,
  CasesSentToCourtAnalytics,
  CasesSuspendedAnalytics,
  ReturnedCasesAnalytics,
  OvertimeCasesAnalytics,
  AccusedPersonsAnalytics,
  RehabilitatedPersonsAnalytics
} from "@/types/analytics";

interface MapAnalyticsProps {
  paramName: MapDisplayParamsKey;
  selectedMonth: Month;
  selectedYear: number;
  selectedRegion: Region | null;
  selectedDistrict: District | null;
}

// TODO: Передать на бек, что бы привели все к единообразию, затем вынести в loadTotalData и loadTopRegions. 19.12.2025.
// TODO: regions => region. federal_district => district, federal_districts => district, districts => district.
// Активный тип аналитики. (Россия, Округа, Регионы).
const activeType = ref<LocationType>("russia");
// Установить активный тип аналитики
const setAnalyticsActiveType = () => {
  // Определяем активный тип аналитики в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    activeType.value = "russia";
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    activeType.value = "district";
  } else {
    // 3) Запрос по региону
    activeType.value = "region";
  }
};
/////////////////////

const props = defineProps<MapAnalyticsProps>();

const analyticStore = useAnalyticStore();

// Perfect Scrollbar
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

onMounted(async () => {
  await loadAnalyticsData();
  updateScroll();
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

// Обновление скролла
const updateScroll = () => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });
  }
};

// Проверяем наличие изменения выбранного параметра, округа или региона для обновления данных.
watch(
  () => [props.paramName, props.selectedDistrict, props.selectedRegion, props.selectedYear, props.selectedMonth],
  async () => {
    await loadAnalyticsData();
    updateScroll();
  }
);

const loadAnalyticsData = async () => {
  setAnalyticsActiveType();
  await Promise.all([
    loadTotalData(),
    loadTopRegions(),
    loadDynamic()
  ]);
};

// Загрузка статичн
const loadTotalData = async () => {
  const baseParams = {
    year: props.selectedYear,
    month: props.selectedMonth,
  };

  let queryParams: TotalMetricDataQuery;

  // Определяем тип запроса и параметры в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    queryParams = { ...baseParams, type: "russia" };
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    queryParams = { ...baseParams, type: "federal_district", district: props.selectedDistrict };
  } else {
    // 3) Запрос по региону
    queryParams = { ...baseParams, type: "region", region: props.selectedRegion ?? undefined };
  }

  const response = await analyticStore.getAnalyticMetricData(queryParams, props.paramName);

  Object.assign(analyticsData, {
    totalData: response
  });
};



// Загрузка динамики данных помесячно
const loadDynamic = async () => {
  let type: "russia" | "districts" | "regions";
  const queryParams: DynamicQuery = {
    metricName: props.paramName,
    year: props.selectedYear
  };

  // Определяем тип запроса и параметры в зависимости от выбранного округа/региона
  if (props.selectedDistrict === null && props.selectedRegion === null) {
    // 1) Запрос по России
    type = "russia";
  } else if (props.selectedDistrict !== null && props.selectedRegion === null) {
    // 2) Запрос по округу
    type = "districts";
    queryParams.district = props.selectedDistrict;
  } else {
    // 3) Запрос по региону
    type = "regions";
    queryParams.region = props.selectedRegion ?? undefined;
  }

  const response = await analyticStore.getDynamicByMonth(queryParams, type);

  Object.assign(analyticsData, {
    monthlyDynamic: response
  });
};

// Загрузка ТОП-10 регионов с наибольшими/наименьшими показателями.
const loadTopRegions = async () => {
  const baseQueryParams: RegionTopQuery = {
    metricName: props.paramName,
    year: props.selectedYear,
    month: props.selectedMonth,
    count: 10
  };

  const [topAscending, topDescending] = await Promise.all([
    analyticStore.getTopRegions({ ...baseQueryParams, sortOrder: "ascending" }),
    analyticStore.getTopRegions({ ...baseQueryParams, sortOrder: "descending" })
  ]);

  Object.assign(analyticsData, { topAscending, topDescending });
};

const analyticsData: AnalyticsState = reactive({
  topAscending: null,
  topDescending: null,
  monthlyDynamic: null,
  totalData: null
});
</script>

<template>
  <div
    ref="scrollContainer"
    class="analytics"
  >
    <CasesAccepted
      v-if="paramName === 'cases_accepted'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as CasesAcceptedAnalytics"
    />
    <CasesSentToCourt
      v-else-if="paramName === 'cases_sent_to_court_with_indictment'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as CasesSentToCourtAnalytics"
    />
    <CasesSuspended
      v-else-if="paramName === 'cases_suspended'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as CasesSuspendedAnalytics"
    />
    <CasesReturned
      v-else-if="paramName === 'cases_returned_for_investigation'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as ReturnedCasesAnalytics"
    />
    <CasesCompleted
      v-else-if="paramName === 'cases_completed_overtime'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as OvertimeCasesAnalytics"
    />
    <AccusedInCourt
      v-else-if="paramName === 'accused_in_court_cases'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as AccusedPersonsAnalytics"
    />
    <PersonsRehabilitated
      v-else-if="paramName === 'persons_rehabilitated'"
      :active-type="activeType"
      :selected-region="selectedRegion"
      :selected-district="selectedDistrict"
      :analytic-data="analyticsData as RehabilitatedPersonsAnalytics"
    />
  </div>
</template>

<style scoped lang="scss">

.analytics {
  height: calc(100% - 60px);
  overflow: hidden;
  padding-right: 20px;
  padding-left: 10px;
  position: absolute;
  right: -20px;
  //top: 60px;
  bottom: 0;
  width: 480px;
  z-index: 9;
}


// Кастомизация perfect-scrollbar
// Вертикальный скролл
// Полоса прокрутки
:deep(.ps__rail-y) {
  opacity: 1 !important;
  width: 14px !important;
  right: 0;
  box-sizing: border-box;
}

// Ползунок прокрутки
:deep(.ps__thumb-y) {
  background-color: #1A64D433 !important;
  border-radius: 30px !important;
  width: 7px !important;
  right: 5px !important;
}
</style>